import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { notify } from "../../../services/toast.config";

import type { AppDispatch } from "../../../core/data/redux/store";
import {
  selectReferrals,
  selectReferralsLoading,
  selectJobs,
} from "../../../core/data/redux/recruitment/selectors";
import {
  fetchMyReferrals,
  fetchJobs,
} from "../../../core/data/redux/recruitment/thunks";

import ReferralFilters, {
  type ReferralFiltersState,
} from "./components/ReferralFilters";
import ReferralTable from "./components/ReferralTable";
import ReferralForm from "./components/ReferralForm";
import RecruitmentHeader from "../components/RecruitmentHeader";
import Footer from "../components/Footer";

const EmployeeReferrals: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();

  const referrals = useSelector(selectReferrals);
  const loading = useSelector(selectReferralsLoading);
  const jobs = useSelector(selectJobs);
  const activeCompany = useSelector((state: any) => state.recruitment.activeCompany);

  const [filters, setFilters] = useState<ReferralFiltersState>({
    search: "",
    status: "",
    date_from: undefined,
    date_to: undefined,
  });
  const [showForm, setShowForm] = useState(false);

  useEffect(() => {
    if (!activeCompany?.company_id) return;
    dispatch(fetchMyReferrals());
    dispatch(fetchJobs({ limit: 100 }));
  }, [dispatch, activeCompany?.company_id]);

  const handleFilterChange = useCallback(
    (partial: Partial<ReferralFiltersState>) => {
      const next = { ...filters, ...partial };
      setFilters(next);

      if ("status" in partial || "date_from" in partial || "date_to" in partial) {
        dispatch(
          fetchMyReferrals({
            status: next.status || undefined,
          }),
        );
      }
    },
    [dispatch, filters],
  );

  const filtered = useMemo(() => {
    const q = filters.search.trim().toLowerCase();
    if (!q) return referrals;
    return referrals.filter(
      (r) =>
        r.candidate_name?.toLowerCase().includes(q) ||
        r.candidate_email?.toLowerCase().includes(q) ||
        r.job_title?.toLowerCase().includes(q),
    );
  }, [referrals, filters.search]);

  const handleReferralSubmitted = useCallback(() => {
    dispatch(fetchMyReferrals());
  }, [dispatch]);

  return (
    <div className="page-wrapper">
      <div className="content">
        <RecruitmentHeader title="Refer Candidates" breadcrumb="Refer Candidates" />

        <div
          style={{
            background: "#fff",
            borderRadius: 10,
            boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
            marginBottom: 16,
          }}
        >
          <div style={{ padding: 20 }}>
            <ReferralFilters
              filters={filters}
              onFilterChange={handleFilterChange}
              loading={loading}
              onReferClick={() => setShowForm(true)}
            />
            <div className="mt-4">
              <ReferralTable
                data={filtered}
                loading={loading}
              />
            </div>
          </div>
        </div>
      </div>

      <ReferralForm
        show={showForm}
        onClose={() => setShowForm(false)}
        jobs={jobs}
        onSubmitted={handleReferralSubmitted}
      />

      <Footer />
    </div>
  );
};

export default EmployeeReferrals;
